import { Box, IconButton } from '@chakra-ui/react';
import { ReactNode, memo, useMemo } from 'react';
import { MdNavigateBefore, MdNavigateNext, MdOutlineFirstPage, MdOutlineLastPage } from 'react-icons/md';

import SelectField from '../../select-field';
import { Txt } from '../../text';

type TablePaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange(page: number): void;
  onPageSizeChange(page: number): void;
  footerAdditional?: ReactNode
}

const TablePaginationComponent = ({ page, pageSize, total, onPageChange, onPageSizeChange, footerAdditional }: TablePaginationProps) => {
  
  const lastPage = useMemo(
    () => Math.ceil(total / pageSize),
    [total, pageSize]
  );

  const pages: number[] = useMemo(() => {
    const pagesRangeGenerator = function* () {
      let i = 1;
      while (i <= lastPage) {
        // check a page is at the beginning or at the end or around the current page
        if (i >= page - 3 && i <= page + 3) {
          yield i;
        }
        i++;
      }
    };
    return [...pagesRangeGenerator()];
  }, [page, lastPage]);

  const onPrevPageClick = () => {
    page !== 1 && onPageChange(page - 1);
  };

  const onNextPageClick = () => {
    page !== lastPage && onPageChange(page + 1);
  };
  
  return <Box px={6} py={4} >
    <Box display='flex' gap={8} alignItems='center' flexWrap="wrap"> 
      <Box display='flex' gap={4} alignItems='center'>
        <Txt>Отображать на странице:</Txt> 
        <Box width='70px'>
          <SelectField 
            id='page-size'
            value={{ id: pageSize, name: pageSize.toString() }}
            options={pageSizeOptions}   
            clearable={false}   
            onChange={({ id }) => onPageSizeChange(Number(id))}
            size='sm'
          />
        </Box>
      </Box>
      <Box>
        <Txt>{`${(page - 1)*pageSize + 1}-${Math.min(total, page * pageSize)} из ${total}`}</Txt>
      </Box>
      <Box display='flex' gap={2} alignItems='center'>
        <IconButton size='sm' icon={<MdOutlineFirstPage />} aria-label="first-page" onClick={() => onPageChange(1)}     />
        <IconButton size='sm' 
          icon={<MdNavigateBefore />} aria-label="prev-page" 
          opacity={page === 1 ? 0.5 : 1} onClick={onPrevPageClick} 
        />
        {pages.map((_page, index) => (
            <IconButton
              size='sm'
              key={`${_page}-${index}`}
              _active={{
                backgroundColor: 'brand.500',
                color: 'white',
              }}
              isActive={_page === page}
              onClick={() => onPageChange(_page)}
              icon={<Txt fontSize='sm'>{_page}</Txt>}
              aria-label={`page-${_page}`}
              
            />
          ))}
        <IconButton size='sm' 
          icon={<MdNavigateNext />} aria-label="next-page"
          opacity={page === lastPage ? 0.5 : 1} onClick={onNextPageClick} 
        />
        <IconButton size='sm' icon={<MdOutlineLastPage />} aria-label="last-page" onClick={() => onPageChange(lastPage)}/>
      </Box>
      {footerAdditional}
    </Box>
  </Box>;
};

export const TablePagination = memo(TablePaginationComponent);

const pageSizeOptions = [
  { id: 10, name: '10' },
  { id: 20, name: '20' },
  { id: 50, name: '50' },
  { id: 100, name: '100' },
];
